/**
 * A pick-one dropdown styled like the rest of the app, for when the native
 * <select> menu looks out of place (it is drawn by the OS). A button opens a
 * listbox under it: ↑ ↓ move, Enter or Space picks, Escape or a click outside
 * closes. Options can be grouped under section headers, carry a muted hint and
 * be disabled. Long lists (more than 8) get a filter box at the top. The list
 * floats above the page (a portal), so a dialog's scrolling body never clips
 * it, and it opens upward when there is no room below.
 *
 * Use this for every pick-one dropdown, so they all look and behave the same.
 */

import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import { Check, ChevronDown } from "lucide-react";
import { Floating } from "./floating";

export interface SelectOption {
  value: string;
  label: string;
  section?: string | undefined;
  disabled?: boolean | undefined;
  /** Muted text on the right, e.g. a count or version. */
  hint?: string | undefined;
}

const SEARCH_FROM = 8;
function sections(options: SelectOption[]): Array<[string, SelectOption[]]> {
  const out = new Map<string, SelectOption[]>();
  for (const o of options) out.set(o.section ?? "", [...(out.get(o.section ?? "") ?? []), o]);
  return [...out];
}

export function Select({
  id: buttonId,
  label,
  value,
  onChange,
  options,
  placeholder = "Select…",
  disabled = false,
  invalid = false,
  required = false,
  className = "",
  buttonClassName = "",
}: {
  /** Set it to name the button with a visible `<label htmlFor>` instead of `label`. */
  id?: string | undefined;
  /** The accessible name, when no `<label htmlFor={id}>` names it. */
  label?: string | undefined;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean | undefined;
  invalid?: boolean | undefined;
  required?: boolean | undefined;
  className?: string;
  /** Extra classes for the button, e.g. a height (default h-9 like other inputs). */
  buttonClassName?: string;
}) {
  const id = useId();
  const [query, setQuery] = useState("");
  const button = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const searchable = options.length > SEARCH_FROM;
  const q = query.trim().toLowerCase();
  const grouped = sections(
    q ? options.filter((o) => `${o.label} ${o.hint ?? ""}`.toLowerCase().includes(q)) : options,
  );
  const flat = grouped.flatMap(([, os]) => os);
  const current = options.find((o) => o.value === value);
  const optionId = (i: number) => `${id}-o${i}`;

  useEffect(() => {
    if (open && active >= 0)
      document.getElementById(optionId(active))?.scrollIntoView?.({ block: "nearest" });
  });

  const show = () => (setQuery(""), setOpen(true), setActive(options.findIndex((o) => o.value === value)));
  const close = () => (setOpen(false), button.current?.focus());
  const pick = (o: SelectOption) => {
    if (o.disabled) return;
    // Close first, so `onChange` can move focus on (e.g. back into a text box).
    close();
    onChange(o.value);
  };
  /** The next enabled option from `from` in direction `step`, or `from` if none. */
  const move = (from: number, step: number) => {
    for (let i = from + step; i >= 0 && i < flat.length; i += step) if (!flat[i]!.disabled) return i;
    return from;
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) return show();
      setActive((a) => move(a, e.key === "ArrowDown" ? 1 : -1));
    } else if ((e.key === "Enter" || (e.key === " " && !searchable)) && open) {
      e.preventDefault();
      const o = flat[active];
      if (o) pick(o);
    } else if (e.key === "Escape" && open) {
      e.preventDefault();
      e.stopPropagation();
      close();
    }
  };

  let n = -1;
  return (
    <div className={`relative min-w-0 ${className}`}>
      <button
        ref={button}
        id={buttonId}
        type="button"
        disabled={disabled}
        aria-label={label}
        aria-invalid={invalid || undefined}
        aria-required={required || undefined}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? `${id}-list` : undefined}
        aria-activedescendant={open && active >= 0 ? optionId(active) : undefined}
        className={`inp flex w-full items-center justify-between gap-2 text-left disabled:cursor-not-allowed disabled:opacity-60 ${buttonClassName}`}
        onClick={() => (open ? setOpen(false) : show())}
        onKeyDown={onKeyDown}
      >
        <span className={`truncate ${current ? "" : "text-faint"}`}>{current?.label ?? placeholder}</span>
        <ChevronDown
          size={14}
          className={`shrink-0 text-faint transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <Floating anchor={button} onDismiss={() => setOpen(false)} className="flex flex-col">
          {searchable && (
            <div className="border-b border-line-soft p-1.5">
              <input
                autoFocus
                aria-label="Filter options"
                aria-controls={`${id}-list`}
                aria-activedescendant={active >= 0 ? optionId(active) : undefined}
                className="inp h-8 w-full text-[13px]"
                placeholder="Filter…"
                value={query}
                onChange={(e) => (setQuery(e.target.value), setActive(0))}
                onKeyDown={onKeyDown}
              />
            </div>
          )}
          <ul
            id={`${id}-list`}
            role="listbox"
            aria-label={label}
            aria-labelledby={label ? undefined : buttonId}
            className="overflow-auto p-1"
          >
            {flat.length === 0 && <li className="px-2 py-1.5 text-[13px] text-muted">No matches</li>}
            {grouped.map(([section, os]) => (
              <li key={section} role="presentation">
                {section && (
                  <div className="px-2 pt-2 pb-1 text-[11px] font-semibold tracking-[0.04em] whitespace-nowrap text-faint uppercase">
                    {section}
                  </div>
                )}
                <ul role="group" aria-label={section || undefined}>
                  {os.map((o) => {
                    const i = ++n;
                    const selected = o.value === value;
                    return (
                      <li
                        key={o.value}
                        id={optionId(i)}
                        role="option"
                        aria-selected={selected}
                        aria-disabled={o.disabled || undefined}
                        className={`flex items-center gap-2 rounded-md py-1.5 pr-3 pl-2 text-[13px] ${
                          o.disabled
                            ? "cursor-not-allowed text-faint"
                            : `cursor-pointer ${i === active ? "bg-nav-hover text-ink" : "text-body"}`
                        }`}
                        onMouseDown={(e) => (e.preventDefault(), pick(o))}
                        onMouseMove={() => !o.disabled && i !== active && setActive(i)}
                      >
                        <span className="flex w-3.5 shrink-0 justify-center">
                          {selected && <Check size={13} strokeWidth={2.5} className="text-ink" />}
                        </span>
                        <span
                          title={o.label}
                          className={`truncate ${selected ? "font-medium text-ink" : ""}`}
                        >
                          {o.label}
                        </span>
                        {o.hint && <span className="ml-auto shrink-0 pl-3 text-xs text-faint">{o.hint}</span>}
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>
        </Floating>
      )}
    </div>
  );
}
